<script lang="ts" setup>
import {PhArrowRight} from "@phosphor-icons/vue";
import ErrorDisplayConcise from "~/components/ErrorDisplayConcise.vue";
import type {AspectRatio} from "#base-layer/utils/parseAspectRatio";
import type {UseOffsetPaginationReturn} from "@vueuse/core";
import {FetchError} from "ofetch";
import type {Delivery} from "#magnolia-layer/types/api";
import type {Page} from "#magnolia-layer/types/magnolia-types";

defineProps<{
  content: Delivery<Page> | null;
  offsetPagination: UseOffsetPaginationReturn;
  error?: FetchError | null;
}>();

const articleOnGrid = [
  {
    cssClass: "col-span-12 xl:col-span-10 flex-col lg:flex-row lg:*:min-w-80",
    aspectRatio: "16/9",
  },
  {cssClass: "col-span-5 xl:col-span-4 flex-col", aspectRatio: "4/3"},
  {cssClass: "col-span-5 xl:col-span-4 flex-col", aspectRatio: "4/3"},
  {
    cssClass: "col-span-5 xl:col-span-4 col-start-3 xl:col-start-5 flex-col",
    aspectRatio: "4/3",
  },
  {cssClass: "col-span-5 xl:col-span-4 flex-col", aspectRatio: "4/3"},
  {
    cssClass:
      "col-span-12 xl:col-span-10 xl:col-start-3 flex-col lg:flex-row-reverse lg:*:min-w-80",
    aspectRatio: "16/9",
  },
  {cssClass: "col-span-5 xl:col-span-4  flex-col", aspectRatio: "4/3"},
  {
    cssClass: "col-span-5 xl:col-span-4 col-start-8 xl:col-start-9  flex-col",
    aspectRatio: "4/3",
  },
] as {cssClass: string; aspectRatio: AspectRatio}[];

const getPageLink = (path: string): string | undefined => {
  if (path) {
    return stripPrefix(path);
  }
  return undefined;
};

const imageExtract = (image: any, aspectRatio: AspectRatio) => {
  const parsedRatio = parseAspectRatio(aspectRatio);
  return parsedRatio ? imageToExtractionArea(image, parsedRatio) : undefined;
};
</script>

<template>
  <section class="col-span-full content-grid">
    <h1 class="sr-only">{{ $t("label.overview") }}</h1>
    <ErrorDisplayConcise v-if="error" :error></ErrorDisplayConcise>
    <div
      v-else
      class="articles flex flex-col lg:inner-content-grid gap-4 items-start">
      <template v-for="(page, index) in content?.results" :key="page['@id']">
        <article
          class="min-w-full flex gap-x-4 flex-2 mb-16"
          :class="articleOnGrid[index].cssClass">
          <NuxtLinkLocale
            v-if="getPageLink(page['@path'])"
            :href="getPageLink(page['@path'])"
            class="text-[inherit] block w-full">
            <MagnoliaPicture
              :asset="page.image"
              :modifiers="{
                extract: imageExtract(
                  page.image,
                  articleOnGrid[index].aspectRatio,
                ),
                grayscale: 1,
              }"
              densities="1x 2x"
              loading="lazy"
              class="*:w-full *:h-full *:object-contain *:object-center"
              sizes="400 md:600 lg:520 xl:640"
              v-if="page.image">
            </MagnoliaPicture>
          </NuxtLinkLocale>
          <div class="article__body">
            <h1 class="uppercase font-sans font-bold mt-4 mb-2">
              <NuxtLinkLocale
                v-if="getPageLink(page['@path'])"
                :href="getPageLink(page['@path'])"
                class="text-[inherit]"
                >{{ page.title }}
              </NuxtLinkLocale>
            </h1>
            <p class="text-width-fit" v-if="page.abstract">
              {{ page.abstract }}
            </p>
            <NuxtLinkLocale
              v-if="getPageLink(page['@path'])"
              class="link-with-icon link-underline mt-4 text-sunrise-200"
              :href="getPageLink(page['@path']) ?? ''">
              {{ $t("label.continueReading") }}
              <PhArrowRight></PhArrowRight>
            </NuxtLinkLocale>
          </div>
        </article>
      </template>
      <div class="col-span-12 mb-16" v-if="content?.results.length === 0">{{ $t('label.noResults') }}</div>
    </div>
    <Pagination
      v-if="offsetPagination.pageCount.value > 1"
      :enable-pages="false"
      :enable-links="true"
      :pagination="offsetPagination"
      :total="content?.total ?? 0"></Pagination>
  </section>
</template>

<style scoped lang="scss"></style>
